import type { ReactNode } from 'react';
import { useModalDialog } from '@/pages/fulfillment/components/useModalDialog';
import { shelfLabel } from '../inventoryView';
import type { AuditTally } from '../inventoryView';

/** One shelf of the walk with rows nobody has counted yet. */
export interface ShelfLeft {
  locationCode: string;
  left: number;
  total: number;
}

export interface FinishAuditModalProps {
  /** What the counted rows already did to the stock: each was written the moment it was saved [SSD-7447]. */
  tally: AuditTally;
  counted: number;
  shelvesLeft: ShelfLeft[];
  busy: boolean;
  onFinish: () => void;
  onClose: () => void;
  notifications?: ReactNode;
  /** A Random check closes a shelf with nothing counted as not checked, so the dialog says so [SSD-7583]. */
  randomCheck?: boolean;
}

/**
 * Closing a live audit. The counts are already in the stock, so nothing here applies anything: it says what was
 * counted, names the shelves with rows left, and closes the rounds. A shelf left unfinished is listed again after
 * closing, so it can be counted in the next audit [SSD-7447].
 */
export const FinishAuditModal = ({
  tally,
  counted,
  shelvesLeft,
  busy,
  onFinish,
  onClose,
  notifications,
  randomCheck = false,
}: FinishAuditModalProps) => {
  const dialog = useModalDialog(onClose);
  const rowsLeft = shelvesLeft.reduce((sum, shelf) => sum + shelf.left, 0);
  const untouched = shelvesLeft.filter((shelf) => shelf.left === shelf.total).length;

  return (
    <dialog
      className="inv-modal audit-result-modal"
      ref={dialog}
      aria-label="Finish the stock audit"
      onClick={(event) => event.target === event.currentTarget && onClose()}
    >
      <header>
        Finish Stock Audit
        <button type="button" className="x" aria-label="Close finish audit" onClick={onClose}>
          ✕
        </button>
      </header>
      <div className="body">
        <p>
          {`${counted} ${counted === 1 ? 'row was counted and is' : 'rows were counted and are'} already in the stock: ${
            tally.adjustments
          } ${tally.adjustments === 1 ? 'adjustment' : 'adjustments'} (−${tally.decreases} / +${tally.increases}).`}
        </p>
        {rowsLeft === 0 ? (
          <div className="note">Every row of the audit was counted.</div>
        ) : (
          <>
            <div className="note warn">
              {`${rowsLeft} ${rowsLeft === 1 ? 'row' : 'rows'} on ${shelvesLeft.length} ${
                shelvesLeft.length === 1 ? 'shelf' : 'shelves'
              } ${rowsLeft === 1 ? 'was' : 'were'} not counted. They keep the stock the book shows; count them in the next audit.${
                randomCheck && untouched > 0
                  ? ` ${untouched} ${
                      untouched === 1 ? 'shelf has' : 'shelves have'
                    } nothing counted and will close as not checked, so the next Random check draws ${
                      untouched === 1 ? 'it' : 'them'
                    } again.`
                  : ''
              }`}
            </div>
            <table className="tbl audit-left">
              <thead>
                <tr>
                  <th>Location</th>
                  <th>Rows left</th>
                </tr>
              </thead>
              <tbody>
                {shelvesLeft.map((shelf) => (
                  <tr key={shelf.locationCode}>
                    <td>
                      <span className="loc-pill">{shelfLabel(shelf.locationCode)}</span>
                    </td>
                    <td className="num">{`${shelf.left} of ${shelf.total}`}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
      <div className="foot">
        <button type="button" className="btn btn-gray btn-sm" onClick={onClose}>
          Keep counting
        </button>
        <button type="button" className="btn btn-blue btn-sm" disabled={busy} onClick={onFinish}>
          Finish Audit
        </button>
      </div>
      {notifications}
    </dialog>
  );
};
